import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";
import { logDev } from "@/lib/log";

const DISMISS_KEY = "ts_pwa_dismissed_v1";

/**
 * PWA install banner.
 * Listens for the browser's `beforeinstallprompt` and lets the user add
 * the app to their home-screen with a single tap. Also registers the
 * service worker so mobile browsers offer their native install prompt.
 * Auto-hides on iOS (which uses Safari's own Share → "Add to Home Screen").
 */
export default function PwaInstall() {
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Register the service worker (also used by Web Push notifications).
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch((err) => logDev(err));
    }
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === "1"); } catch (err) { logDev(err); }

    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setPrompt(null); toast.success("Trading Secret installed!"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } catch (err) { logDev(err); }
    setPrompt(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (err) { logDev(err); }
  };

  if (!prompt || dismissed) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed z-40 left-4 right-4 sm:left-auto sm:right-6 bottom-24 sm:bottom-6 sm:w-[360px] rounded-2xl border border-slate-800 bg-slate-900 text-white p-4 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)]"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#00b4ff]/15 grid place-items-center text-[#00b4ff] shrink-0">
          <Download size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm tracking-tight">Add to Home Screen</div>
          <p className="mt-1 text-xs text-slate-300 leading-relaxed">
            Install Trading Secret sebagai app — launch dari home screen, faster access, push signals langsung.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="inline-flex items-center gap-1.5 bg-[#00b4ff] hover:bg-[#0090cc] text-white text-xs font-mono-plex tracking-widest uppercase px-4 py-2 rounded-full"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <Download size={12} /> Install
            </button>
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              className="text-xs font-mono-plex tracking-widest uppercase px-4 py-2 rounded-full border border-slate-700 hover:bg-slate-800 text-slate-300"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Nanti
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="text-slate-400 hover:text-white p-1 -mr-1 -mt-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
